/**
 * Derived Intelligence Engine
 *
 * Transforms accumulated snapshots (possession, momentum, xG, pressure,
 * sequences) into derived insights, predictions and narrative WITHOUT
 * requiring sub-second realtime polling or per-event AI calls.
 *
 * Design constraints:
 *  - Pure deterministic math; no I/O, no AI.
 *  - Operates on rolling windows (2 min / 5 min / 1H / 2H).
 *  - Tolerates 5-20s API delay: snapshots can be sparse.
 *  - Cheap: O(N) over capped buffers (N <= 240).
 */

export interface MatchSample {
  /** ms epoch when sampled */
  t: number;
  /** match minute 0..120 */
  minute: number;
  /** -1..1, positive = home dominates */
  momentum: number;
  /** 0..1 home possession share */
  possessionHome: number;
  /** 0..1 pressure on each side (attacking pressure being exerted) */
  pressureHome: number;
  pressureAway: number;
  /** instantaneous xG rate 0..1 */
  xgHomeRate: number;
  xgAwayRate: number;
  /** ball x position in pitch coords -50..50 (positive = away half) */
  ballX: number;
  /** boolean flags */
  inDanger: boolean;
  isShot: boolean;
  isGoal: boolean;
  /** score diff from home perspective */
  scoreDiff: number;
}

export type WindowKey = "w2m" | "w5m" | "h1" | "h2";

export interface WindowDerivative {
  key: WindowKey;
  samples: number;
  avgMomentum: number;       // -1..1
  avgPossessionHome: number; // 0..1
  avgPressureHome: number;   // 0..1
  avgPressureAway: number;   // 0..1
  xgHomeAccum: number;       // accumulated xG estimate
  xgAwayAccum: number;
  attackBurstHome: number;   // consecutive samples with home pressure > 0.6
  attackBurstAway: number;
  dangerCount: number;
  shotCount: number;
  /** -1 away dominant, +1 home dominant */
  territorialDominance: number;
}

export interface DerivedSnapshot {
  generatedAt: number;
  minute: number;
  scoreDiff: number;
  windows: Record<WindowKey, WindowDerivative>;
  /** Higher-level derived signals */
  signals: {
    momentumShift: number;          // delta between w5m and w2m, -2..2
    pressureExtreme: boolean;       // any side > 0.75 sustained 2m
    territorialDominance: number;   // -1..1
    fatigueIndex: number;           // 0..1 (rises late game)
    goalProbabilityHome: number;    // 0..1 next ~5 min
    goalProbabilityAway: number;    // 0..1
    defensiveRiskHome: number;      // 0..1 risk OF conceding
    defensiveRiskAway: number;
    dominantSide: "home" | "away" | "balanced";
    narrativeIntensity: number;     // 0..1 — how dramatic the moment is
  };
}

const MAX_SAMPLES = 600; // ~20 min @ 2Hz, plenty for 120 min @ low rate

export class DerivedIntelligenceEngine {
  private samples: MatchSample[] = [];
  private lastSnapshot: DerivedSnapshot | null = null;

  ingest(sample: MatchSample): void {
    this.samples.push(sample);
    if (this.samples.length > MAX_SAMPLES) {
      this.samples.splice(0, this.samples.length - MAX_SAMPLES);
    }
  }

  /** Returns the cached snapshot or recomputes if older than `maxAgeMs`. */
  snapshot(now: number = Date.now(), maxAgeMs = 2000): DerivedSnapshot {
    if (this.lastSnapshot && now - this.lastSnapshot.generatedAt < maxAgeMs) {
      return this.lastSnapshot;
    }
    const last = this.samples[this.samples.length - 1];
    const minute = last?.minute ?? 0;
    const scoreDiff = last?.scoreDiff ?? 0;

    const windows: Record<WindowKey, WindowDerivative> = {
      w2m: this.windowDerivative("w2m", now, 2 * 60_000, 0, 120),
      w5m: this.windowDerivative("w5m", now, 5 * 60_000, 0, 120),
      h1: this.windowDerivative("h1", now, Infinity, 0, 45),
      h2: this.windowDerivative("h2", now, Infinity, 45, 120),
    };

    const momentumShift = windows.w2m.avgMomentum - windows.w5m.avgMomentum;
    const pressureExtreme =
      windows.w2m.avgPressureHome > 0.75 || windows.w2m.avgPressureAway > 0.75;
    const territorialDominance = windows.w5m.territorialDominance;
    const fatigueIndex = Math.min(1, Math.max(0, (minute - 55) / 55));

    // Goal probability (next ~5 min) — derived heuristic combining recent
    // xG accumulation, pressure burst and possession.
    const goalProbabilityHome = clamp01(
      windows.w2m.xgHomeAccum * 0.55 +
        windows.w2m.avgPressureHome * 0.25 +
        Math.max(0, windows.w2m.avgMomentum) * 0.2,
    );
    const goalProbabilityAway = clamp01(
      windows.w2m.xgAwayAccum * 0.55 +
        windows.w2m.avgPressureAway * 0.25 +
        Math.max(0, -windows.w2m.avgMomentum) * 0.2,
    );
    const defensiveRiskHome = clamp01(goalProbabilityAway * 0.9 + fatigueIndex * 0.1);
    const defensiveRiskAway = clamp01(goalProbabilityHome * 0.9 + fatigueIndex * 0.1);

    let dominantSide: "home" | "away" | "balanced" = "balanced";
    if (territorialDominance > 0.2) dominantSide = "home";
    else if (territorialDominance < -0.2) dominantSide = "away";

    const narrativeIntensity = clamp01(
      Math.abs(momentumShift) * 0.4 +
        Math.max(goalProbabilityHome, goalProbabilityAway) * 0.45 +
        (pressureExtreme ? 0.15 : 0),
    );

    const snap: DerivedSnapshot = {
      generatedAt: now,
      minute,
      scoreDiff,
      windows,
      signals: {
        momentumShift,
        pressureExtreme,
        territorialDominance,
        fatigueIndex,
        goalProbabilityHome,
        goalProbabilityAway,
        defensiveRiskHome,
        defensiveRiskAway,
        dominantSide,
        narrativeIntensity,
      },
    };
    this.lastSnapshot = snap;
    return snap;
  }

  reset(): void {
    this.samples = [];
    this.lastSnapshot = null;
  }

  private windowDerivative(
    key: WindowKey,
    now: number,
    spanMs: number,
    minMinute: number,
    maxMinute: number,
  ): WindowDerivative {
    const cutoff = now - spanMs;
    const inWindow = this.samples.filter(
      (s) => s.t >= cutoff && s.minute >= minMinute && s.minute <= maxMinute,
    );
    const n = inWindow.length;
    if (n === 0) {
      return {
        key,
        samples: 0,
        avgMomentum: 0,
        avgPossessionHome: 0.5,
        avgPressureHome: 0,
        avgPressureAway: 0,
        xgHomeAccum: 0,
        xgAwayAccum: 0,
        attackBurstHome: 0,
        attackBurstAway: 0,
        dangerCount: 0,
        shotCount: 0,
        territorialDominance: 0,
      };
    }
    let sumM = 0,
      sumP = 0,
      sumPH = 0,
      sumPA = 0,
      xgH = 0,
      xgA = 0,
      burstH = 0,
      burstA = 0,
      curH = 0,
      curA = 0,
      danger = 0,
      shots = 0,
      sumBallX = 0;
    for (const s of inWindow) {
      sumM += s.momentum;
      sumP += s.possessionHome;
      sumPH += s.pressureHome;
      sumPA += s.pressureAway;
      xgH += s.xgHomeRate;
      xgA += s.xgAwayRate;
      sumBallX += s.ballX;
      if (s.pressureHome > 0.6) curH++;
      else {
        if (curH > burstH) burstH = curH;
        curH = 0;
      }
      if (s.pressureAway > 0.6) curA++;
      else {
        if (curA > burstA) burstA = curA;
        curA = 0;
      }
      if (s.inDanger) danger++;
      if (s.isShot) shots++;
    }
    burstH = Math.max(burstH, curH);
    burstA = Math.max(burstA, curA);
    return {
      key,
      samples: n,
      avgMomentum: sumM / n,
      avgPossessionHome: sumP / n,
      avgPressureHome: sumPH / n,
      avgPressureAway: sumPA / n,
      // approximate accumulated xG: sum of instantaneous rates / sample rate
      xgHomeAccum: clamp01(xgH / Math.max(8, n)),
      xgAwayAccum: clamp01(xgA / Math.max(8, n)),
      attackBurstHome: burstH,
      attackBurstAway: burstA,
      dangerCount: danger,
      shotCount: shots,
      // ballX > 0 = away half → home attacking → positive = home dominant.
      territorialDominance: clamp(sumBallX / n / 50, -1, 1),
    };
  }
}

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}
function clamp(x: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, x));
}

/** Singleton for the active match. War Room and panels share it. */
let _singleton: DerivedIntelligenceEngine | null = null;
export function getDerivedIntelEngine(): DerivedIntelligenceEngine {
  if (!_singleton) _singleton = new DerivedIntelligenceEngine();
  return _singleton;
}